import { Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { FieldAttributes, FieldKind } from "./fields-html"
import type { CommandProps } from "@tiptap/core"
import type { DOMOutputSpec } from "@tiptap/pm/model"
import {
	emptyFieldAttributes,
	fieldSpec,
	isFieldLocation,
	parseLabelList,
	readFieldAttributes,
} from "./fields-html"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		apiFields: {
			insertParamField: (attrs?: Partial<FieldAttributes>) => ReturnType
			insertResponseField: (attrs?: Partial<FieldAttributes>) => ReturnType
		}
	}
}

export const PARAM_FIELD_NODE = "paramField"
export const RESPONSE_FIELD_NODE = "responseField"

const NODE_KIND: Record<string, FieldKind> = {
	[PARAM_FIELD_NODE]: "param",
	[RESPONSE_FIELD_NODE]: "response",
}

export const fieldKindOf = (nodeName: string): FieldKind =>
	NODE_KIND[nodeName] ?? "response"

export function fieldAttributesOf(
	nodeName: string,
	attrs: Record<string, unknown>,
): FieldAttributes {
	const kind = fieldKindOf(nodeName)
	const text = (value: unknown) => (typeof value === "string" ? value : "")
	const labels = (value: unknown) =>
		Array.isArray(value)
			? value.filter((item): item is string => typeof item === "string")
			: []
	return {
		name: text(attrs.name),
		location:
			kind === "param" && isFieldLocation(attrs.location)
				? attrs.location
				: null,
		fieldType: text(attrs.fieldType),
		required: attrs.required === true,
		deprecated: attrs.deprecated === true,
		defaultValue:
			typeof attrs.defaultValue === "string" ? attrs.defaultValue : null,
		placeholder:
			kind === "param" && typeof attrs.placeholder === "string"
				? attrs.placeholder
				: null,
		pre: labels(attrs.pre),
		post: labels(attrs.post),
	}
}

// Every attribute is written by the node's own renderHTML, so each one opts
// out of Tiptap's per-attribute rendering.
const skipRender = { rendered: false } as const

function createFieldNode(name: string, kind: FieldKind) {
	const dataType = kind === "param" ? "param-field" : "response-field"
	return Node.create({
		name,
		group: "block",
		content: "block+",
		defining: true,
		isolating: true,

		addAttributes() {
			const read = (element: HTMLElement) => readFieldAttributes(kind, element)
			const attributes: Record<string, object> = {
				name: {
					default: "",
					parseHTML: (el: HTMLElement) => read(el).name,
					...skipRender,
				},
				fieldType: {
					default: "string",
					parseHTML: (el: HTMLElement) => read(el).fieldType,
					...skipRender,
				},
				required: {
					default: false,
					parseHTML: (el: HTMLElement) => read(el).required,
					...skipRender,
				},
				deprecated: {
					default: false,
					parseHTML: (el: HTMLElement) => read(el).deprecated,
					...skipRender,
				},
				defaultValue: {
					default: null,
					parseHTML: (el: HTMLElement) => read(el).defaultValue,
					...skipRender,
				},
				pre: {
					default: [],
					parseHTML: (el: HTMLElement) =>
						parseLabelList(el.getAttribute("data-pre")),
					...skipRender,
				},
				post: {
					default: [],
					parseHTML: (el: HTMLElement) =>
						parseLabelList(el.getAttribute("data-post")),
					...skipRender,
				},
			}
			if (kind === "param") {
				attributes.location = {
					default: "query",
					parseHTML: (el: HTMLElement) => read(el).location ?? "body",
					...skipRender,
				}
				attributes.placeholder = {
					default: null,
					parseHTML: (el: HTMLElement) => read(el).placeholder,
					...skipRender,
				}
			}
			return attributes
		},

		parseHTML() {
			return [
				{
					tag: `div[data-type="${dataType}"]`,
					// The header is drawn from the attributes, so only the body is content.
					contentElement: (dom: globalThis.Node) =>
						(Array.from((dom as HTMLElement).children).find((child) =>
							child.classList.contains("pl-field-body"),
						) as HTMLElement | undefined) ?? (dom as HTMLElement),
				},
			]
		},

		renderHTML({ node }) {
			return fieldSpec(
				kind,
				fieldAttributesOf(name, node.attrs),
			) as DOMOutputSpec
		},

		addCommands() {
			const insert =
				(attrs: Partial<FieldAttributes> = {}) =>
				({ chain, state }: CommandProps) => {
					const from = state.selection.from
					const initial = emptyFieldAttributes(kind, attrs)
					return chain()
						.insertContent({
							type: this.name,
							attrs: { ...initial },
							content: [{ type: "paragraph" }],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							// The caret goes into the description, below the inline name input.
							let target = -1
							tr.doc.descendants((child, pos) => {
								if (target >= 0) return false
								if (child.type.name === this.name && pos >= from - 1) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target + 2)))
							}
							return true
						})
						.run()
				}
			return kind === "param"
				? { insertParamField: insert }
				: { insertResponseField: insert }
		},
	})
}

export const ParamFieldExtension = createFieldNode(PARAM_FIELD_NODE, "param")
export const ResponseFieldExtension = createFieldNode(
	RESPONSE_FIELD_NODE,
	"response",
)

export const FieldsExtensions = [ParamFieldExtension, ResponseFieldExtension]
